<script setup lang="ts">
import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, ref } from "vue";
import OT from "@opentok/client";

import { VIDEO_CALL_STATUS, VIDEO_CALL_EXCEPTIONS } from "./definitions";
import { VIDEO_CALL_COMPLETE_REASON } from "@/Modules/Models/Api/VideoCalls";

import useVideoCallStore from "../Store";
import useUserStore from "@/Modules/User/Store";

import { log as logger } from "@/Utils/Debug";
import { isApp, isIosApp } from "@/Utils/Ionic";
import { hasValue } from "@/Utils/Objects";

import { MIN_SCALE } from "./constants";

import CallSession from "./components/Session.vue";
import Touch from "./components/Touch.vue";

import { isValidString } from "@/Utils/String";
import { MILLISECONDS_TO_SECONDS } from "@/Utils/Date";

export interface WrapperVonageProps {
  isHidden: boolean;
}

const MAX_WATING_SECONDS = 90 * MILLISECONDS_TO_SECONDS; // 90 seconds

const props = defineProps<WrapperVonageProps>();

const emit = defineEmits<{
  (event: "onShowChat"): void;
}>();

const videoCallStore = useVideoCallStore();
const userStore = useUserStore();

const scale = ref<number>(MIN_SCALE);
const isRatingRequired = ref<boolean>(false);

let timeoutTimer: NodeJS.Timeout | null = null;

const log = (...args: unknown[]) => {
  logger(`[VIDEO_CALL]`, ...args);
};

const vonageToken = computed((): string => videoCallStore.token!);

const isZoomEnabled = computed(() => isApp() && videoCallStore.status === VIDEO_CALL_STATUS.CALLING);

const handleError = ({ code, exception }: { code: VIDEO_CALL_EXCEPTIONS; exception?: unknown }) => {
  log("Video call error", code, exception);

  if (code === VIDEO_CALL_EXCEPTIONS.CONNECT_SESSION_RETRY) {
    handleReject();
  }

  videoCallStore.addError(code, exception);
};

const handleStreamSubscribed = () => {
  log("To Calling");

  videoCallStore.updateStatus(VIDEO_CALL_STATUS.CALLING);

  if (videoCallStore.showConfig && userStore.getUserIsPatient) {
    nextTick(() => videoCallStore.updateShowConfig(false));
  }

  if (isRatingRequired.value) {
    videoCallStore.enableRating();
  }

  if (hasValue(timeoutTimer)) {
    clearTimeout(timeoutTimer!);
    timeoutTimer = null;
  }
};

const handleReject = (reason = VIDEO_CALL_COMPLETE_REASON.REJECTED) => {
  log("REJECT VIDEOCALL");

  handleEnd(reason);
};

const handleHangDown = (reason: VIDEO_CALL_COMPLETE_REASON = VIDEO_CALL_COMPLETE_REASON.OK) => {
  log("HANG DOWN VIDEOCALL");

  handleEnd(reason);
};

const handleEnd = async (reason: VIDEO_CALL_COMPLETE_REASON) => {
  log("END VIDEO CALL");

  videoCallStore.updateStatus(VIDEO_CALL_STATUS.CLOSING);

  await videoCallStore.hangDownVideoCall(videoCallStore.roomId, reason);
};

const handlePublisherReady = () => {
  log("TO WAITING");

  videoCallStore.updateStatus(VIDEO_CALL_STATUS.WAITING);

  if (videoCallStore.showConfig && userStore.getUserIsTherapist) {
    nextTick(() => videoCallStore.updateShowConfig(false));
  }

  timeoutTimer = setTimeout(() => {
    if (videoCallStore.status !== VIDEO_CALL_STATUS.CALLING) {
      handleEnd(VIDEO_CALL_COMPLETE_REASON.MISSED);
    }
  }, MAX_WATING_SECONDS);
};

const handleUpdateScale = (newScale: number) => {
  scale.value = newScale;
};

onBeforeMount(async () => {
  log("Before mount video call");

  const config = videoCallStore.getConfig();

  if (hasValue(config) && isValidString(config.outputId)) {
    OT.setAudioOutputDevice(config.outputId);
  }
});

onMounted(() => {
  log("Mounted video call");

  window.addEventListener("beforeunload", videoCallStore.clean);

  isRatingRequired.value = !!videoCallStore.isRatingRequired;

  videoCallStore.disableRating();

  timeoutTimer = null;
  videoCallStore.updateStatus(VIDEO_CALL_STATUS.PERMISSIONS);

  OT.on("exception", (event) => {
    log("Exception", { message: event.message, name: event.title });
  });
});

onUnmounted(() => {
  log("Unmounted videocall");

  if (hasValue(timeoutTimer)) {
    clearTimeout(timeoutTimer!);
    timeoutTimer = null;
  }
});
</script>

<template>
  <Touch
    v-show="
      !props.isHidden &&
      ![VIDEO_CALL_STATUS.PERMISSIONS, VIDEO_CALL_STATUS.CLOSING].includes(videoCallStore.status!)
    "
    v-model="scale"
    id="video-call-wrapper"
    class="call"
    :class="{ 'call--ios_app': isIosApp() }"
    :isZoomEnabled="isZoomEnabled"
  >
    <CallSession
      class="call__session"
      :token="vonageToken"
      :sessionId="videoCallStore.sessionId!"
      :transcription="videoCallStore.isTranscriptionEnabled"
      :scale="scale"
      @streamSubscribed="handleStreamSubscribed"
      @publisherReady="handlePublisherReady"
      @error="handleError"
      @showChat="emit('onShowChat')"
      @endCall="handleHangDown"
      @rejectCall="handleReject"
      @updateScale="handleUpdateScale"
    />
  </Touch>
</template>

<style scoped lang="scss">
.call {
  position: absolute;
  background-color: white;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: -webkit-fill-available;
  height: 100dvh;
  padding-top: var(--ion-safe-area-top);
  padding-bottom: var(--ion-safe-area-bottom);
  overflow: hidden;
  z-index: 10001;

  &__session {
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 1;
  }
}
</style>
